import Vue from "vue";
import { Config } from "@/shared/config";
import {
  CommunicationClient,
  CommunicationPortal,
  MfaStatusEnum,
  AssetType,
  UserRole,
  HsabPlanType,
  EplLookupMethod,
  FileUploadCategory,
  EmploymentStatus
 } from "@/shared/types";
import { PartnerContentType } from "@/admin/types";

const SearchUsersBase = "/user/v1/user/search?";
const User: string = Config.UserServicebaseUri + "/user/v1/user";
const GetEmployerUser: string = Config.UserServicebaseUri + "/user/v1/user/search";
const EmployeeBase = Config.UserServicebaseUri + "/user/v1/employee";
const EmployerBase = Config.UserServicebaseUri + "/user/v1/employer";

export default {
  postEmployerUser: (employerId: number) => `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/users`,
  getEmployerUsers: (employerId: number) => `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/users`,
  putEmployerUser: (employerId: number, employerUserId: number) =>
  `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/user/${employerUserId}`,
  getUser: () => User,
  createUser: () => User,
  searchUserByEmployerId (employerId: number): string {
    return Config.UserServicebaseUri + SearchUsersBase + `employerId=${employerId}`;
  },
  searchUserByEmail (email: string): string {
    return Config.UserServicebaseUri + SearchUsersBase + `email=${email}`;
  },
  getInitiateImpersonationResponse (): string {
    return Config.UserServicebaseUri + `/user/v1/impersonation/tokens`;
  },
  getImpersonationUrl (targetCognitoUsername: string, partnerId: number): string {
    return Config.UserServicebaseUri
      + `/user/v1/impersonation/${partnerId}/url?targetCognitoUsername=${targetCognitoUsername}`;
  },
  employeesList (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/employees-list`;
  },
  markUserAsVerified (username: string): string {
    return Config.UserServicebaseUri + `/user/v1/user/${username}/verify-email`;
  },
  resendUserActivation (userId: number, employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/user/${userId}/${employerId}/resend-activation`;
  },
  deleteEmployerUser (userId: number, employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/user/${userId}/${employerId}/delete`;
  },
  recoverUser () {
    return Config.UserServicebaseUri + `/user/v1/recoverUser`;
  },
  profileInformation (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/profile`;
  },
  fundingOption (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/fundingOption`;
  },
  fundingOptionHistory (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/fundingOption-history`;
  },
  family (): string {
    return `${Config.UserServicebaseUri}/user/v1/user/family`;
  },
  familyMember (familyId: number): string {
    return `${Config.UserServicebaseUri}/user/v1/user/family/${familyId}`;
  },
  getEmployeeInformation (employerId: number, employeeId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/employee/${employeeId}`;
  },
  saveEmployeeInformation (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/employee`;
  },
  cancelEmployeePlanUpdate (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/cancelemployeeupdate`;
  },
  getEmployerSearch (): string {
    return Config.UserServicebaseUri +
      `/user/v1/employer/search`;
  },
  employeeClaimBegin (verificationCode: string) {
    return Config.UserServicebaseUri + `/user/v1/claim/begin?verificationCode=${encodeURIComponent(verificationCode)}`;
  },
  employeeClaimEnd (verificationCode: string) {
    return Config.UserServicebaseUri + `/user/v1/claim/end?verificationCode=${encodeURIComponent(verificationCode)}`;
  },
  employeeClaimAddEmployeeGroupToUser () {
    return Config.UserServicebaseUri + `/user/v1/claim/add-employee-group`;
  },
  employeeClaimCheckEmailAvailability () {
    return Config.UserServicebaseUri + `/user/v1/claim/checkEmailAvailability`;
  },
  activateEmployer (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/activate`;
  },
  terminationReasons (): string {
    return Config.UserServicebaseUri + `/user/v1/termination-reasons`;
  },
  terminateEmployer (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/terminate`;
  },
  cancelScheduledTermination (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/cancel-termination-schedule`;
  },
  reactivateEmployer (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/reactivate`;
  },
  inviteEmployees (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/invite/${employerId}`;
  },
  employer (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}`;
  },
  getUsStates (): string {
    return Config.UserServicebaseUri + `/user/v1/states`;
  },
  employerDashboard (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/dashboard/${employerId}`;
  },
  persona () {
    return Config.UserServicebaseUri + `/user/v1/user/persona`;
  },
  earliestInviteDate (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/earliestinvitesenddate`;
  },
  wizardState (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/wizard`;
  },
  resendEmployeeInviteLink (employerId: number, employeeId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/invite/${employerId}/resendinvite/${employeeId}`;
  },
  userTermsAndConditions (): string {
    return Config.UserServicebaseUri + "/user/v1/user/terms";
  },
  getAcceptedUserTermsAndConditions () {
    return Config.UserServicebaseUri + "/user/v1/user/accepted-terms";
  },
  contactInformation (): string {
    return Config.UserServicebaseUri + "/user/v1/user/contactdetail";
  },
  contactInformationAdmin (): string {
    return Config.UserServicebaseUri + "/user/v1/user/admin/contactdetail";
  },
  userProfile () {
    return Config.UserServicebaseUri + "/user/v1/user/profile";
  },
  userEmailUpdate (partnerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/partner/${partnerId}/update-user-email`;
  },
  userWizardProgress (): string {
    return Config.UserServicebaseUri + "/user/v1/user/onboardingprogress";
  },
  userWizardProgressComplete (step: number) {
    return Config.UserServicebaseUri + `/user/v1/user/onboardingprogress/${step}`;
  },
  employerProgressComplete (employerId: number, step: number) {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/onboardingprogress/${step}`;
  },
  employerCensusUpload (employerId: number) {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/employer-census-upload`;
  },
  initialAnnualContribution () {
    return Config.UserServicebaseUri + "/user/v1/user/getcontributionamountfromcensus";
  },
  verifyEmailChange () {
    return Config.UserServicebaseUri + `/user/v1/email-verify`;
  },
  sendHsabEmailChangeVerificationCode () {
    return Config.UserServicebaseUri + "/user/v1/hsab/email-verify/send-verification-code";
  },
  verifyHsabEmailChange () {
    return Config.UserServicebaseUri + "/user/v1/hsab/email-verify/verify";
  },
  userAccountAlerts () {
    return Config.UserServicebaseUri + "/user/v1/notifications";
  },
  deleteUserAccountAlert (notificationId: number): string {
    return Config.UserServicebaseUri + `/user/v1/notifications/${notificationId}/delete`;
  },
  termsAccepted () {
    return Config.UserServicebaseUri + "/user/v1/user/termsaccepted";
  },
  planType () {
    return Config.UserServicebaseUri + "/user/v1/plandetails/planType";
  },
  userCoverageLevels () {
    return Config.UserServicebaseUri + "/user/v1/plandetails/coveragelevels";
  },
  createIndividualSignup () {
    return Config.UserServicebaseUri + "/user/v1/individualsignup";
  },
  verifyIndividualSignup () {
    return Config.UserServicebaseUri + "/user/v1/individualsignup/verify";
  },
  resendIndividualSignup () {
    return Config.UserServicebaseUri + "/user/v1/individualsignup/resend";
  },
  individualSignupCreateLogin () {
    return Config.UserServicebaseUri + "/user/v1/individualsignup/create-login-account";
  },
  individualSignupLoginEvent () {
    return Config.UserServicebaseUri + "/user/v1/individualsignup/login-event";
  },
  individualSignupCip (): string {
    return Config.UserServicebaseUri + "/user/v1/individualsignup/cip";
  },
  putUserEmployerName (): string {
    return Config.UserServicebaseUri + "/user/v1/individualsignup/employer-name";
  },
  individualSignupEmailClick (): string {
    return Config.UserServicebaseUri + "/user/v1/individualsignup/email-click";
  },
  planDetails: () => Config.UserServicebaseUri + "/user/v1/plandetails",
  contributionLimits () {
    return `${Config.UserServicebaseUri}/user/v1/contribution-limit`;
  },
  legalContributionLimitsByPlanType () {
    return `${Config.UserServicebaseUri}/user/v1/individualsignup/hsacontributionlimits`;
  },
  otherHsaAmount () {
    return Config.UserServicebaseUri + "/user/v1/other-hsa-amount";
  },
  adminPartners () {
    return Config.UserServicebaseUri + "/user/v1/partner";
  },
  adminPartner (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}`;
  },
  adminPartnerSettings (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/settings`;
  },
  employerPartnerSettings (partnerId: number, employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/${employerId}/employer-partner-settings`;
  },
  adminSftpTestFiles () {
    return `${Config.UserServicebaseUri}/user/v1/admin/sftp-test-files`;
  },
  adminSftpTestFilesDownload () {
    return `${this.adminSftpTestFiles()}/download`;
  },
  getPartnerUsers (
    partnerId: number,
    pageNumber,
    searchFirstname: string,
    searchLastname: string,
    searchEmail: string,
    searchRole: UserRole) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/users?page=${pageNumber}&searchFirstName=${encodeURIComponent(searchFirstname)}&searchLastName=${encodeURIComponent(searchLastname)}&searchEmail=${encodeURIComponent(searchEmail)}&searchRole=${searchRole}`;
  },
  savePartnerUser (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/user`;
  },
  updatePartnerUserData (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/admin/user`;
  },
  reportEmbed (id: string) {
    return `${Config.UserServicebaseUri}/user/v1/reportEmbed/${id}`;
  },
  partnerAsset (partnerId: number, assetType: AssetType) {
    return `${Config.UserServicebaseUri}/user/v1/partner/asset/${partnerId}/${assetType}`;
  },
  partnerContent (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/content/${partnerId}`;
  },
  userPartnerContent (contentType: PartnerContentType) {
    return `${Config.UserServicebaseUri}/user/v1/partner/content?contentType=${encodeURIComponent(contentType)}`;
  },
  partnerContentDisplay (partnerId: number, contentType: PartnerContentType) {
    return `${Config.UserServicebaseUri}/user/v1/partner/content/${partnerId}/display?contentType=${encodeURIComponent(contentType)}`;
  },
  PartnerSupportInfo (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/support-info`;
  },
  resendPartnerUserActivation (partnerId: number, inviteId: number): string {
    return Config.UserServicebaseUri + `/user/v1/partner/${partnerId}/resend-activation/${inviteId}`;
  },
  partnerUserForgotPassword (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/forgot-password`;
  },
  partnerUserDisableMfa (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/disable-mfa`;
  },
  partnerAddEmployer (partnerId: number): string {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/employers`;
  },
  conversionClaimAccount () {
    return `${Config.UserServicebaseUri}/user/v1/user/claim-online-account`;
  },
  logHsaStoreClick () {
    return `${Config.UserServicebaseUri}/user/v1/audit/hsa/store`;
  },
  logHsaBlueBookClick () {
    return `${Config.UserServicebaseUri}/user/v1/audit/hsa/bluebook`;
  },
  logExpenseAttachmentUploaded () {
    return `${Config.UserServicebaseUri}/user/v1/audit/expense/uploaded`;
  },
  logEmployeeLogin () {
    return `${Config.UserServicebaseUri}/user/v1/audit/login/employee`;
  },
  logEmployerAccess () {
    return `${Config.UserServicebaseUri}/user/v1/audit/login/employer`;
  },
  logHsaStoreWidget (isDefaultLink: boolean) {
    return `${Config.UserServicebaseUri}/user/v1/audit/hsa/storewidget/${isDefaultLink}`;
  },
  partnerEmployersSearch (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/employers-search`;
  },
  partnerEmployers (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/employers`;
  },
  partnerEmployer (partnerId: number, employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/employers/${employerId}`;
  },
  downloadEmployeeBankInfoReport (employerId: number) {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/employee-hsainfo-report`;
  },
  getEmployeeSearchResults (partnerId: number) {
    return Config.UserServicebaseUri + `/user/v1/partner/${partnerId}/findemployee`;
  },
  getAdminParticipantSearchResults () {
    return Config.UserServicebaseUri + `/user/v1/participant/search`;
  },
  downloadFraudPreventionReport () {
    return Config.UserServicebaseUri + `/user/v1/admin/fraud-prevention-report`;
  },
  downloadDemoUsersReport () {
    return Config.UserServicebaseUri + `/user/v1/admin/demo-users-report`;
  },
  ssoValidateAccount (): string {
    return Config.UserServicebaseUri + `/user/v1/elements-sso/validate`;
  },
  samlToken (code: string): string {
    return Config.UserServicebaseUri + `/user/v1/saml/token?code=${code}`;
  },
  putRevealPersonalInformation (): string {
    return Config.UserServicebaseUri + `/user/v1/reveal`;
  },
  putAdminPortalRevealPersonalInformation (): string {
    return Config.UserServicebaseUri + `/user/v1/reveal/adminportal`;
  },
  userAccountEnable (): string {
    return Config.UserServicebaseUri + `/user/v1/cognito/enableuser`;
  },
  userAccountDisable (): string {
    return Config.UserServicebaseUri + `/user/v1/cognito/disableuser`;
  },
  resetUserEmail (): string {
    return Config.UserServicebaseUri + `/user/v1/cognito/reset-email`;
  },
  getUserById (userId: number) {
    return Config.UserServicebaseUri + `/user/v1/users/${userId}`;
  },
  getUsers (pageNumber: number, pageSize: number, searchTerm: string) {
    return Config.UserServicebaseUri +
      `/user/v1/users?page=${pageNumber}&pageSize=${pageSize}` +
      `&searchTerm=${encodeURIComponent(searchTerm)}`;
  },
  saveAdminDobNameChange () {
    return Config.UserServicebaseUri + `/user/v1/admin-dob-name-change`;
  },
  getEmployeeSearchList (employerId: number, searchTerm: string): string {
    return Config.UserServicebaseUri +
      `/user/v1/employer/${employerId}/employeesSearchList?searchTerm=${encodeURIComponent(searchTerm)}`;
  },
  getBirthYear (): string {
    return Config.UserServicebaseUri + `/user/v1/user/birthyear`;
  },
  hsaStoreInfo (partnerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/partnerhsastore/${partnerId}`;
  },
  communicationKitType (partnerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/partner/${partnerId}/communication-kit-type`;
  },
  logoutRedirect (partnerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/partner/${partnerId}/logout-redirect`;
  },
  sendMfaStatus (status: MfaStatusEnum, phoneNumber: string) {
    const phomeNumberOrEmptyString = (phoneNumber && phoneNumber.length) ? `?phoneNumber=${phoneNumber}` : "";
    return `${Config.UserServicebaseUri}/user/v1/user/mfaupdate/${status}${phomeNumberOrEmptyString}`;
  },
  setMfaPreference (value: boolean) {
    return `${Config.UserServicebaseUri}/user/v1/user/mfapreference/${value}`;
  },
  getSalesPersons () {
    return `${Config.UserServicebaseUri}/user/v1/salesperson`;
  },
  saveSalesPerson () {
    return `${Config.UserServicebaseUri}/user/v1/salesperson/save`;
  },
  employerRolePermissions () {
    return `${Config.UserServicebaseUri}/user/v1/employer-role-permission`;
  },
  partnerNotificationEmail (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/notification-email`;
  },
  requestDisableMfa () {
    return `${Config.UserServicebaseUri}/user/v1/mfa/requestDisable`;
  },
  disableMfaWithCode () {
    return `${Config.UserServicebaseUri}/user/v1/mfa/disable`;
  },
  downloadCensusResultFile (employerId: number, censusFileId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/census-files/${censusFileId}/result`;
  },
  employerUpdateCensusSetting (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/employerUpdateCensusSetting`;
  },
  censusManager (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/censusManager`;
  },
  getPartnerSettings () {
    return `${Config.UserServicebaseUri}/user/v1/user/partnersettings`;
  },
  getEmployerCensusFiles (employerId: number, pageNumber: number, pageSize: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/census-files/${pageNumber}?pageSize=${pageSize}`;
  },
  getEmployerCensusFileDetails (employerId: number, censusFileId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/census-file/${censusFileId}`;
  },
  getEmployeeIdFiles (employerId: number, pageNumber: number, pageSize: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/employeeid-files/${pageNumber}?pageSize=${pageSize}`;
  },
  postEmployeeIdUpdateFile (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/employeeid-savefile`;
  },
  fetchEmployeeIdUpdateFile (employerId: number, employeeIdFileId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/employeeid-files/${employeeIdFileId}/result`;
  },
  getRolePermissions () {
    return `${Config.UserServicebaseUri}/user/v1/user/permissions`;
  },
  updateEmployerUser (employerId: number, employerUserId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/user/${employerUserId}/update`;
  },
  quickSightReport () {
    return `${Config.UserServicebaseUri}/user/v1/quicksight-report`;
  },
  quickSightReportById (reportId: number) {
    return `${Config.UserServicebaseUri}/user/v1/quicksight-report/${reportId}`;
  },
  getUserRoles () {
    return `${Config.UserServicebaseUri}/user/v1/roles`;
  },
  getUserRolesByType (roleType: string) {
    return `${Config.UserServicebaseUri}/user/v1/roles/getByType/${roleType}`;
  },
  getRolesByType (roleType: string) {
    return `${Config.UserServicebaseUri}/user/v1/roles/getRolesByType/${roleType}`;
  },
  createUserRole () {
    return `${Config.UserServicebaseUri}/user/v1/roles/create`;
  },
  updateRole () {
    return `${Config.UserServicebaseUri}/user/v1/roles/update`;
  },
  getUserRoleById (roleId: number) {
      return `${Config.UserServicebaseUri}/user/v1/roles/getById/${roleId}`;
  },
  UpdatePermissionsByRole () {
    return `${Config.UserServicebaseUri}/user/v1/role-permission/updateRolePermissionsByRole`;
  },
  getReportCategories () {
    return `${Config.UserServicebaseUri}/user/v1/quicksight-report/report-categories`;
  },
  getEmployerReports (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/quicksight-report/${employerId}/employer-reports`;
  },
  getPartnerReports (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/quicksight-report/${partnerId}/partner-reports`;
  },
  getTestUsers () {
    return `${Config.UserServicebaseUri}/user/v1/admin/test-user`;
  },
  signingDocuments () {
    return `${Config.UserServicebaseUri}/user/v1/document`;
  },
  saveFile () {
    return `${Config.UserServicebaseUri}/user/v1/document/file`;
  },
  signingDocumentList () {
    return `${Config.UserServicebaseUri}/user/v1/document/list`;
  },
  getSigningDocument (documentKey: string, version: number) {
    return `${Config.UserServicebaseUri}/user/v1/document/key/${documentKey}/${version}`;
  },
  getSigningDocumentAdmin (documentKey: string, version: number) {
    return `${Config.UserServicebaseUri}/user/v1/document/key/${documentKey}/${version}/admin`;
  },
  getSigningDocumentById (documentId: number, version: number) {
    return `${Config.UserServicebaseUri}/user/v1/document/id/${documentId}/${version}`;
  },
  getSigningDocumentByIdAdmin (documentId: number, version: number) {
    return `${Config.UserServicebaseUri}/user/v1/document/id/${documentId}/${version}/admin`;
  },
  getDocumentLatestVersion (documentKey: string) {
    return `${Config.UserServicebaseUri}/user/v1/document/${documentKey}`;
  },
  getDocumentLatestVersionAdmin (documentKey: string) {
    return `${Config.UserServicebaseUri}/user/v1/document/${documentKey}/admin`;
  },
  getUserSignedDocumentKey (accountType: string) {
    return `${Config.UserServicebaseUri}/user/v1/document/user-signed-document-key-by-accountType/${accountType}`;
  },
  getUserSignedDocumentKeyAdmin () {
    return `${Config.UserServicebaseUri}/user/v1/document/user-signed-document-key/admin`;
  },
  getSignature (documentKey: string) {
    return `${Config.UserServicebaseUri}/user/v1/document-signature?documentKey=${documentKey}`;
  },
  saveSignature () {
    return `${Config.UserServicebaseUri}/user/v1/document-signature`;
  },
  getCommunication (id: number) {
    return `${Config.UserServicebaseUri}/user/v1/communications/${id}`;
  },
  getEmployeeCommunications () {
    return `${Config.UserServicebaseUri}/user/v1/communications/employee`;
  },
  getEmployerCommunications (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/communications/employer?employerId=${employerId}`;
  },
  partnerCommunications (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/communication`;
  },
  employerCommunications (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/communication`;
  },
  communicationImage (partnerId: number, communicationId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/communication/${communicationId}/image`;
  },
  partnerCommunicationsForPortal (partnerId: number, portal: CommunicationPortal) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/communication/portal/${portal}`;
  },
  employerCommunicationsForPortal (employerId: number, portal: CommunicationPortal) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/communication/portal/${portal}`;
  },
  partnerCommunicationEmployers (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/communication/employers`;
  },
  deleteSignature (signatureKey: string) {
    return `${Config.UserServicebaseUri}/user/v1/document-signature/${signatureKey}`;
  },
  markDeleteSignature (signatureKey: string) {
    return `${Config.UserServicebaseUri}/user/v1/document-signature/delete/${signatureKey}`;
  },
  userEvent () {
    return `${Config.UserServicebaseUri}/user/v1/event`;
  },
  userLoginEvent () {
    return `${Config.UserServicebaseUri}/user/v1/event/login`;
  },
  pwned () {
    return `${Config.UserServicebaseUri}/user/v1/check-password`;
  },
  getSearchCapability () {
    return `${Config.UserServicebaseUri}/user/v1/opensearch/capability`;
  },
  openSearchAdminIndexes () {
    return `${Config.UserServicebaseUri}/user/v1/opensearch/indexes`;
  },
  openSearchDomainDetails () {
    return `${Config.UserServicebaseUri}/user/v1/opensearch/domain-details`;
  },
  get uploadSynonymFile () {
    return `${Config.UserServicebaseUri}/user/v1/opensearch/upload`;
  },
  openSearchReindexUser (cognitoUsername: string) {
    return `${Config.UserServicebaseUri}/user/v1/opensearch/index-user/${cognitoUsername}`;
  },
  openSearchReindexEmployer (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/opensearch/index-employer/${employerId}`;
  },
  openSearchGetEmployerDocument (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/opensearch/employer/${employerId}`;
  },
  openSearchGetUserDocument (cognitoUsername: string) {
    return `${Config.UserServicebaseUri}/user/v1/opensearch/user/${cognitoUsername}`;
  },
  getMonitoredStateMachines (maxHistoryCount: number) {
    return `${Config.UserServicebaseUri}/user/v1/admin-state-machine/monitored?maxResults=${maxHistoryCount}`;
  },
  enrollmentOption (employerId: number): string {
    return Config.UserServicebaseUri + `/user/v1/employer/${employerId}/enrollment-option`;
  },
  automatedFundingOptionLeaveDate (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/fundingOption-automatedFundingOptionLeaveDate`;
  },
  resetUserCustodian () {
    return `${Config.UserServicebaseUri}/user/v1/admin-reset-user-custodian`;
  },
  resetPassword () {
    return `${Config.UserServicebaseUri}/user/v1/admin-password-reset`;
  },
  outageNotification () {
    return `${Config.UserServicebaseUri}/user/v1/outage-notification`;
  },
  identityProofingOptions (username: string) {
    return `${Config.UserServicebaseUri}/user/v1/hsab/identity-options/${encodeURIComponent(username)}`;
  },
  identityProofingChallenge () {
    return `${Config.UserServicebaseUri}/user/v1/hsab/identity-challenge`;
  },
  mfaEmailChallenge () {
    return `${Config.UserServicebaseUri}/user/v1/hsab/mfa-email-challenge`;
  },
  mfaEmailVerify () {
    return `${Config.UserServicebaseUri}/user/v1/hsab/verify-mfa-email`;
  },
  identityProofingVerify () {
    return `${Config.UserServicebaseUri}/user/v1/hsab/identity-verify`;
  },
  identityProofingDataVerify () {
    return `${Config.UserServicebaseUri}/user/v1/hsab/data-identity-verify`;
  },
  claimHsabAccount () {
    return `${Config.UserServicebaseUri}/user/v1/hsab/claim-account`;
  },
  claimLoginEvent () {
    return Config.UserServicebaseUri + "/user/v1/hsab/claim-login-event";
  },
  idpInfoProfile (partnerId: number, username: string) {
    return `${Config.UserServicebaseUri}/user/v1/idp-info/${partnerId}/profile/${encodeURIComponent(username)}`;
  },
  idpInfoChangeStatus (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/idp-info/${partnerId}/change-status`;
  },
  idpInfoChangeSuspendStatus (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/idp-info/${partnerId}/change-suspend-status`;
  },
  sendIdpEmailVerificationMessage (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/idp-info/${partnerId}/send-verify-email-message`;
  },
  verifyIdpEmailChange () {
    return Config.UserServicebaseUri + `/user/v1/email-verify/idp`;
  },
  oktaForgotPassword () {
    return `${Config.UserServicebaseUri}/user/v1/okta/forgot-password`;
  },
  oktaForgotUsername () {
    return `${Config.UserServicebaseUri}/user/v1/okta/forgot-username`;
  },
  oktaAccountExists (username: string) {
    return `${Config.UserServicebaseUri}/user/v1/okta/account/${encodeURIComponent(username)}/exists`;
  },
  oktaFactors () {
    return `${Config.UserServicebaseUri}/user/v1/okta/factors`;
  },
  oktaFactor (factorId: string) {
    return `${Config.UserServicebaseUri}/user/v1/okta/factors/${factorId}`;
  },
  resetFactor (factorId: string) {
    return `${Config.UserServicebaseUri}/user/v1/okta/factors/${factorId}/reset`;
  },
  enrollFactor () {
    return `${Config.UserServicebaseUri}/user/v1/okta/factors`;
  },
  revert () {
    return `${Config.UserServicebaseUri}/user/v1/okta/factors/revert`;
  },
  activateFactor (factorId: string) {
    return `${Config.UserServicebaseUri}/user/v1/okta/factors/${factorId}/activate`;
  },
  getEmploymentStatus (cognitoUsername: string) {
    return `${Config.UserServicebaseUri}/user/v1/employer/employment-status/${cognitoUsername}`;
  },
  mobileAppAccessCheck (appIdentifier: string, username: string) {
    return `${Config.UserServicebaseUri}/user/v1/mobile/${encodeURIComponent(appIdentifier)}/check-user-access?username=${encodeURIComponent(username)}`;
  },
  mobileAppVersionCheck (appIdentifier: string) {
    return `${Config.UserServicebaseUri}/user/v1/mobile/${encodeURIComponent(appIdentifier)}/check-version`;
  },
  searchMobileApps () {
    return `${Config.UserServicebaseUri}/user/v1/mobile/search-mobile-apps`;
  },
  getMobileApp (appId: number) {
    return `${Config.UserServicebaseUri}/user/v1/mobile/${encodeURIComponent(appId)}`;
  },
  updateMobileApp () {
    return `${Config.UserServicebaseUri}/user/v1/mobile/update`;
  },
  createMobileApp () {
    return `${Config.UserServicebaseUri}/user/v1/mobile/create`;
  },
  deleteMobileApp (appId: number) {
    return `${Config.UserServicebaseUri}/user/v1/mobile/${encodeURIComponent(appId)}`;
  },
  validateUsername () {
    return `${Config.UserServicebaseUri}/user/v1/idp-info/username-validation`;
  },
  employerAsset (employerId: number, assetType: AssetType) {
    return `${Config.UserServicebaseUri}/user/v1/asset/employer/${employerId}/${assetType}`;
  },
  userAsset (partnerId: number, employerId: number, assetType: AssetType) {
    return `${Config.UserServicebaseUri}/user/v1/asset/${partnerId}/${assetType}/${employerId}`;
  },
  SsoLinkClick () {
    return `${Config.UserServicebaseUri}/user/v1/hsabank/sso/click`;
  },
  getSupportInfo (partnerId: number, employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/supportinfo/${partnerId}/${employerId}`;
  },
  saveEmployerSupportInfo (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/supportinfo/employer/${employerId}`;
  },
  lobVerify () {
    return `${Config.UserServicebaseUri}/user/v1/address/address-validation`;
  },
  lobAutocomplete () {
    return `${Config.UserServicebaseUri}/user/v1/address/address-autocomplete`;
  },
  getSimpleUser (partnerId: number, username: string) {
    return `${Config.UserServicebaseUri}/user/v1/user/${partnerId}/simple/${encodeURIComponent(username)}`;
  },
  getEmailHistory () {
    return `${Config.UserServicebaseUri}/user/v1/email-history/search`;
  },
  getEmailHistoryRawEventData (id: number) {
    return `${Config.UserServicebaseUri}/user/v1/email-history/raw-event/${id}`;
  },
  removeEmailFromBounceList () {
    return `${Config.UserServicebaseUri}/user/v1/email-history/bounces/remove-list`;
  },
  updateUserIdpUsername () {
    return `${Config.UserServicebaseUri}/user/v1/idp-info/username`;
  },
  updateUserIdpPassword () {
    return `${Config.UserServicebaseUri}/user/v1/idp-info/password`;
  },
  shouldShowEmployerSignupQuestion (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/settings/show-employer-signup-question`;
  },
  ebtoGetCampaign (campaignKey: string) {
    return `${Config.UserServicebaseUri}/user/v1/ebto/campaign/${campaignKey}`;
  },
  ebtoClaimUserCampaign (campaignKey: string) {
    return `${Config.UserServicebaseUri}/user/v1/ebto/campaign/${campaignKey}/claim-user`;
  },
  ebtoSendConfirmationEmail (campaignKey: string) {
    return `${Config.UserServicebaseUri}/user/v1/ebto/campaign/${campaignKey}/send-confirmation-email`;
  },
  ebtoPricingOffer (campaignId: string) {
    return `${Config.UserServicebaseUri}/user/v1/ebto/offer/${campaignId}`;
  },
  ebtoAcceptPricingOffer (campaignId: string) {
    return `${Config.UserServicebaseUri}/user/v1/ebto/offer/accept/${campaignId}`;
  },
  ebtoFinalizeOffer (campaignId: string) {
    return `${Config.UserServicebaseUri}/user/v1/ebto/campaign/${campaignId}/user-finalize`;
  },
  resetUserIdpAccount (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/idp-info/${partnerId}/reset`;
  },
  enableUserIdpAccount (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/idp-info/${partnerId}/enable`;
  },
  syncUserIdpAccount (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/idp-info/${partnerId}/sync`;
  },
  getCampaigns () {
    return `${Config.UserServicebaseUri}/user/v1/transferoutcampaign`;
  },
  getEmployerTypeAhead (partnerId: number, searchTerm: string) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/employer-type-ahead?term=${encodeURIComponent(searchTerm)}`;
  },
  ebtoEmployerSearch (searchTerm: string) {
    return `${Config.UserServicebaseUri}/user/v1/employer/ebtosearch?searchTerm=${encodeURIComponent(searchTerm)}`;
  },
  ebtoCreateCampaign () {
    return `${Config.UserServicebaseUri}/user/v1/transferoutcampaign/create-employer-campaign`;
  },
  ebtoUpdateCampaign () {
    return `${Config.UserServicebaseUri}/user/v1/transferoutcampaign/update-employer-campaign`;
  },
  getCampaignDetails (campaignKey: string) {
    return `${Config.UserServicebaseUri}/user/v1/transferoutcampaign/get-campaign-details/?campaignKey=${campaignKey}`;
  },
  ebtoResendVerificationCode (campaignKey: string, userId: string, verificationType: number) {
    return `${Config.UserServicebaseUri}/user/v1/ebto/campaign/${campaignKey}/resend-verification-code/${userId}/${verificationType}`;
  },
  ebtoVerifyUser (campaignKey: string) {
    return `${Config.UserServicebaseUri}/user/v1/ebto/campaign/${campaignKey}/verify-user`;
  },
  eplLookupItem (productCode: string, method: EplLookupMethod) {
    return `${Config.UserServicebaseUri}/user/v1/epl?id=${encodeURIComponent(productCode)}&method=${encodeURIComponent(method)}`;
  },
  getCampaignRetentionDetails (campaignKey: string) {
    return `${Config.UserServicebaseUri}/user/v1/transferoutcampaign/get-campaign-retention-details/?campaignKey=${campaignKey}`;
  },
  getLoginHistory (partnerId: number, username: string) {
    return `${Config.UserServicebaseUri}/user/v1/login-history/${partnerId}?username=${username}`;
  },
  getScannerLicense (): string {
    return Config.UserServicebaseUri + `/user/v1/epl/scanner-license`;
  },
  getUserPermissions () {
    return `${Config.UserServicebaseUri}/user/v1/permission/get-user-permissions`;
  },
  userFeedback () {
    return Config.UserServicebaseUri + "/user/v1/user-feedback";
  },
  getFileUploadCategories () {
    return Config.UserServicebaseUri + "/user/v1/file-upload/categories";
  },
  uploadFiles (category: FileUploadCategory, accountNumber?: string) {
    if (accountNumber) {
      return Config.UserServicebaseUri + `/user/v1/file-upload/category/${category}/account/${accountNumber}`;
    }
    return Config.UserServicebaseUri + `/user/v1/file-upload/category/${category}/account`;
  },
  createAdminUser () {
    return User + `/admin`;
  },
  updateAdminUser () {
    return User + `/update-admin-user`;
  },
  syncAdminUser (userId: number) {
    return User + `/sync-admin-user/${userId}`;
  },
  employerSsoUserSyncStatus (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/sso-user-sync-status`;
  },
  hsabEnrollmentSettings () {
    return `${Config.UserServicebaseUri}/user/v1/hsab-enrollment/settings`;
  },
  getAdminUser () {
    return  `${Config.UserServicebaseUri}/user/v1/internal-user-info/get-adminusers`;
  },
  activeAdminUser () {
    return User + `/activate-admin-user`;
  },
  deactivateAdminUser () {
    return User + `/deactivate-admin-user`;
  },
  revokeUserAccess (userId) {
    return User + `/revoke-admin-user/${userId}`;
  },
  resetPasswordAdminUser () {
    return User + `/reset-password-admin-user`;
  },
  unlockAdminUser () {
    return User + `/unlock-admin-user`;
  },
  searchAdminUsers () {
    return  `${Config.UserServicebaseUri}/user/v1/internal-user-info/search-adminusers`;
  },
  resendActivationEmail () {
    return User + `/resend-activation-email`;
  },
  getUserManagementRolePermissions () {
    return `${Config.UserServicebaseUri}/user/v1/role-permission/getRolePermissions`;
  },
  partnerBulkAddUpload () {
    return `${Config.UserServicebaseUri}/user/v1/partneruser/partner-user-bulk-add-upload`;
  },
  partnerBulkDeleteUpload () {
    return `${Config.UserServicebaseUri}/user/v1/partneruser/partner-user-bulk-delete-upload`;
  },
  partnerBulkGetAllFiles () {
    return `${Config.UserServicebaseUri}/user/v1/partneruser/get-all-files`;
  },
  partnerBulkGetFiles (pageNumber, pageSize, fileType) {
    return `${Config.UserServicebaseUri}/user/v1/partneruser/get-bulk-files/${pageNumber}?pageSize=${pageSize}&fileType=${fileType}`;
  },
  partnerSearchBulkFiles (pageNumber, pageSize, fileType, fileName, status, startDate, endDate) {
    return `${Config.UserServicebaseUri}/user/v1/partneruser/search-bulk-files/${pageNumber}?pageSize=${pageSize}&fileType=${fileType}&fileName=${fileName}&status=${status}&startDate=${startDate}&endDate=${endDate}`;
  },
  downloadPartnerUserBulkResultFile (partnerUserFileId) {
    return `${Config.UserServicebaseUri}/user/v1/partneruser/bulk-files/${partnerUserFileId}/result`;
  },
  userNotes () {
    return `${Config.UserServicebaseUri}/user/v1/user/notes`;
  },
  resyncTalonClaims (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/admin-user-sync/${partnerId}/resync-mms`;
  },
  userEmploymentInfo () {
    return `${Config.UserServicebaseUri}/user/v1/employer/employee`;
  },
  userConfirmed () {
    return `${Config.UserServicebaseUri}/user/v1/user/confirmed`;
  },
  partnerUserMfaOptions (partnerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/partner/${partnerId}/mfa-options`;
  },
  userInterestRates () {
    return `${Config.UserServicebaseUri}/user/v1/interest-rates`;
  },
  employerFeeSchedules () {
    return `${Config.UserServicebaseUri}/user/v1/employer-fee-schedules`;
  },
  employerFeeSchedulesActiveForUser () {
    return `${Config.UserServicebaseUri}/user/v1/employer-fee-schedules/active-for-user`;
  },
  activeEmployerFeeSchedules (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/fee-schedules`;
  },
  employerInterestRates (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/interest-rates`;
  },
  countries () {
    return `${Config.UserServicebaseUri}/user/v1/countries`;
  },
  userDiscretionaryData () {
    return `${Config.UserServicebaseUri}/user/v1/user/discretionary-data`;
  },
  userPersonalInfo () {
    return `${Config.UserServicebaseUri}/user/v1/user/personal-info`;
  },
  getImportantDates (): string {
    return Config.UserServicebaseUri + `/user/v1/important-date`;
  },
  trusteeToTrusteeBalanceTransferForm () {
    return `${Config.UserServicebaseUri}/user/v1/hsa-balance-transfer-form/trustee-to-trustee`;
  },
  rolloverBalanceTransferForm () {
    return `${Config.UserServicebaseUri}/user/v1/hsa-balance-transfer-form/rollover`;
  },
  getMetricsWithContent (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/metricswithcontent`;
  },
  getMetricsWithGraphs (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/metricswithgraph`;
  },
  getEmployerProducts (employerId: number) {
    return `${Config.UserServicebaseUri}/user/v1/employer/${employerId}/products`;
  },
};
